import { useCallback, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { createPortal } from "react-dom"
import { useIsFetching, useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { Box, Stack, Typography } from "@mui/material"
import { useField } from "formik"
import { EventResizeDoneArg } from "@fullcalendar/interaction"
import { DateSelectArg, EventClickArg, EventDropArg, EventHoveringArg } from "@fullcalendar/core"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import {
    EventsForServiceCenterResponse,
    fetchEventsForServiceCenter,
    ConfigQuery,
    fetchBookingOrderDetails,
    BookingOrderDetailsResponse,
    fetchFreeBusyScheduleForResources,
    FreeBusyScheduleForResourcesResponse,
} from "../../../createOrderApi"
import { DateTimeFactory, useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import {
    CalendarLocalEventTooltip,
    CalendarOrderEventTooltip,
    CalendarTooltip,
    CalendarTooltipSkeleton,
} from "./CalendarTooltip"
import { ResourceCalendarLabelContent } from "./ResourceCalendarLabelContent"
import {
    NullableCalendarEvent,
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
} from "../calendar/fullCalendarTypes"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { useResourceDetailsDialog } from "./useResourceDetailsDialog"
import { ResourceDetailsDialog } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"
import {
    createNewEvent,
    DEFAULT_DURATION_MINUTES,
    earliestTime,
    isCanEditExistOrder,
    isLocalEvent,
    mergeUniqueEvents,
    roundDateTimeToNearestMinutes,
    splitIntoThreeDays,
} from "./helpers"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { CreateOrderDialogFlow, ResourceItem } from "../../../createOrderTypes"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { BookPeriodMessageDialog } from "./BookPeriodMessageDialog"
import { AbandonBookingDialog } from "../../AbandonBookingDialog"
import { forceFullRevert, isInPast, isOngoing } from "../calendar/helpers"

const DEFAULT_EVENT_COLOR = "#FCA5A5"
const CAN_EDIT_EVENT_COLOR = "#80CBC4"
const OUT_OF_SERVICE_EVENT_COLOR = "#CBD5E1"

// const transformBookingOrderDetails = (data: BookingOrderDetailsResponse) => {
//     if (!data?.order) {
//         return undefined
//     } else return data.order
// }

const transformFreeBusyScheduleForResources = (
    { resources }: FreeBusyScheduleForResourcesResponse,
    resourceId: string,
) => {
    if (!resources) return false

    const resource = resources.find((r) => r.resource.id === +resourceId)
    if (!resource) return false

    const availability = resource.availabilities.find((slot) => slot.availabilityView)

    if (!availability) return false

    return [...availability.availabilityView].every((c) => c === "0")
}

const transformEventsData = (
    data: EventsForServiceCenterResponse,
    dateTimeFactory: DateTimeFactory,
    localEventUpdateId?: number | null,
) => {
    return (data?.events ?? []).reduce<TimelineCalendarEvent[]>((acc, el) => {
        if (localEventUpdateId && el.orderId === localEventUpdateId) {
            return acc
        }

        acc.push({
            id: String(el.orderId),
            resourceIds: el.resourceIds.map(String),
            start: dateTimeFactory.fromApi(el.startDateTime).toISO(),
            end: dateTimeFactory.fromApi(el.endDateTime).toISO(),
            subject: el?.subject || "",
            host: el?.host || "",
            canEdit: !!el?.canEdit,
            outOfService: !!el?.outOfService,
            backgroundColor: el.outOfService
                ? OUT_OF_SERVICE_EVENT_COLOR
                : el.canEdit
                ? CAN_EDIT_EVENT_COLOR
                : DEFAULT_EVENT_COLOR,
        })

        return acc
    }, [])
}
export type IsResourceAvailableFn = (
    newStart: string,
    newEnd: string,
    resourceIds: string[],
    excludeEventId?: string,
) => boolean

export type ResourceBookingCalendarHoveredEvent = {
    id: string
    top: number
    left: number
    local: boolean
    start?: string
    end?: string
    host?: string
    subject?: string
}

type ResourceBookingCalendarProps = Readonly<{
    timelineResources: TimelineCalendarResource[]
    searchRegex: RegExp | undefined
    groupImagesMap: Map<string, string>
    refreshEventsToken: number
    localEventUpdateId?: number | null
    userId: number
    isBookedPeriod: boolean
    handleChangeOrderContext: (orderId: string) => void
    flowConfig?: CreateOrderDialogFlow
}>

export function ResourceBookingCalendar({
    timelineResources,
    searchRegex,
    groupImagesMap,
    refreshEventsToken,
    localEventUpdateId,
    userId,
    isBookedPeriod,
    handleChangeOrderContext,
    flowConfig,
}: ResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const isDraggingRef = useRef(false)
    const queryClient = useQueryClient()

    const [hoveredEvent, setHoveredEvent] = useState<ResourceBookingCalendarHoveredEvent | null>(null)

    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const { resourceDetails, openResourceDetailsDialog, closeResourceDetailsDialog } = useResourceDetailsDialog()
    const [showBookPeriodDialogMessage, setShowBookPeriodDialogMessage] = useState(false)
    const [confirmAbandonDialogState, setConfirmAbandonDialogState] = useState<{ id: string; title: string } | null>(
        null,
    )
    const [timeChangeDialogState, setTimeChangeDialogState] = useState<{
        resourceId: string
        newStart: string
        newEnd: string
    } | null>(null)

    const [{ value: localEvent }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: fullResources }, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")
    const [{ value: selectedDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const handleChangeLocalEvent = (event: NullableCalendarEvent) => {
        eventHelpers.setValue(event)
        if (!event) {
            fullResourcesHelpers.setValue([])
            return
        }
        const currentFullResources: ResourceItem[] = fullResources ?? []

        const fromTimeline = timelineResources
            .filter((res) => event.resourceIds.includes(res.id))
            .map((res) => ({
                ...res,
                id: Number(res.id),
            }))

        const fromPrevious = currentFullResources
            .filter(
                (res) => event.resourceIds.includes(res.id.toString()) && !fromTimeline.some((r) => r.id === res.id),
            )
            .map((res) => ({
                ...res,
                id: Number(res.id),
            }))

        const merged = [...fromTimeline, ...fromPrevious]

        fullResourcesHelpers.setValue(merged)
    }

    const resourceBufferMap = useMemo(() => {
        return timelineResources.reduce((acc, resource) => {
            acc[resource.id] = resource.buffer || 0
            return acc
        }, {} as Record<string, number>)
    }, [timelineResources])

    const { mutateAsync: callEventsMutation } = useApiMutation(fetchEventsForServiceCenter, {})

    const activeFetches = useIsFetching({
        queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY, userId],
    })
    const isLoadingEvents = activeFetches > 0

    // const { data: bookingOrderDetails, isLoading: isLoadingEventDetails } = useApiQuery(fetchBookingOrderDetails, {
    //     queryName: ConfigQuery.BOOKING_ORDER_DETAILS,
    //     dependencies: { orderId: hoveredEvent?.id ? +hoveredEvent.id : 0 },
    //     select: transformBookingOrderDetails,
    //     enabled: !!hoveredEvent?.id && !hoveredEvent.local,
    // })

    const { mutateAsync: callFreeBusyScheduleForResources } = useApiMutation(fetchFreeBusyScheduleForResources, {})

    const localEventsSource = useMemo(
        () => ({
            id: "local-events",
            events: localEvent ? [localEvent] : [],
        }),
        [localEvent],
    )
    const eventIndexRef = useRef<Record<string, TimelineCalendarEvent[]>>({})

    const createEventSourceFetcher = useCallback(
        (serviceCenterId: string) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                try {
                    const dayRanges = splitIntoThreeDays(fetchInfo.startStr, fetchInfo.endStr)

                    // Query key builder: cache per user + svc + day + refresh token
                    const buildKey = (dayISODate: string) =>
                        [
                            ConfigQuery.EVENTS_LIST_BY_DAY,
                            userId,
                            serviceCenterId,
                            dayISODate,
                            refreshEventsToken,
                        ] as const

                    // fetchQuery will return cached data if fresh, otherwise fetch and cache it
                    const partPromises = dayRanges.map(({ startISO, endISO }) => {
                        const dayISODate = dateTimeFactory.fromISO(startISO).toISODate()! // YYYY-MM-DD
                        return queryClient.fetchQuery({
                            queryKey: buildKey(dayISODate),
                            staleTime: 60_000, // 1 min
                            queryFn: async () =>
                                await callEventsMutation({
                                    userId,
                                    serviceCenterId,
                                    startDate: startISO,
                                    endDate: endISO,
                                }),
                        })
                    })

                    const parts = await Promise.all(partPromises)

                    // Merge, dedupe and transform once
                    const mergedEvents = mergeUniqueEvents(parts)
                    const combined: EventsForServiceCenterResponse = { events: mergedEvents }
                    const transformed = transformEventsData(combined, dateTimeFactory, localEventUpdateId)

                    // Update your existing resource index (unchanged logic)
                    const newIndex: Record<string, TimelineCalendarEvent[]> = {}
                    for (const event of transformed) {
                        for (const resId of event.resourceIds) {
                            if (!newIndex[resId]) newIndex[resId] = []
                            newIndex[resId].push(event)
                        }
                    }
                    for (const [resId, events] of Object.entries(newIndex)) {
                        if (!eventIndexRef.current[resId]) eventIndexRef.current[resId] = []
                        const existing = eventIndexRef.current[resId].filter(
                            (e) => !events.some((ne) => ne.id === e.id),
                        )
                        eventIndexRef.current[resId] = [...existing, ...events]
                    }

                    successCallback(transformed)
                } catch (err) {
                    failureCallback(err)
                }
            },
        [queryClient, callEventsMutation, dateTimeFactory, localEventUpdateId, refreshEventsToken, userId],
    )

    const serverEventsSource = useMemo(() => {
        const uniqueServiceCenterIdList = [...new Set(timelineResources.map((r) => r.locationId.split("_")[0]))]

        return uniqueServiceCenterIdList.map((id) => ({
            id: `source-${id}-${refreshEventsToken}`,
            events: createEventSourceFetcher(id),
        }))
    }, [callEventsMutation, refreshEventsToken])

    const eventSources = useMemo(() => {
        return [localEventsSource, ...serverEventsSource]
    }, [serverEventsSource, localEventsSource])

    const checkOverlap = (newStart: string, newEnd: string, resourceId: string, excludeEventId?: string): boolean => {
        const buffer = resourceBufferMap[resourceId] || 0

        const bufferedStart = dateTimeFactory.fromISO(newStart).minus({ minutes: buffer })
        const bufferedEnd = dateTimeFactory.fromISO(newEnd).plus({ minutes: buffer })

        const relevantEvents = eventIndexRef.current[resourceId] || []
        const allEvents = [...relevantEvents, ...(localEvent?.resourceIds.includes(resourceId) ? [localEvent] : [])]

        return allEvents.some(({ id, start, end }) => {
            if (excludeEventId && id === excludeEventId) return false

            const eventStart = dateTimeFactory.fromISO(start)
            const eventEnd = dateTimeFactory.fromISO(end)

            const isOverlapping =
                (bufferedStart < eventEnd && bufferedStart >= eventStart) ||
                (bufferedEnd > eventStart && bufferedEnd <= eventEnd) ||
                (bufferedStart <= eventStart && bufferedEnd >= eventEnd)

            return isOverlapping
        })
    }

    const isResourceAvailable: IsResourceAvailableFn = (newStart, newEnd, resourceIds, excludeEventId) => {
        return !resourceIds.some((resourceId) => checkOverlap(newStart, newEnd, resourceId, excludeEventId))
    }

    const handleSelect = ({ startStr, endStr, resource }: DateSelectArg) => {
        if (!resource) return

        if (isBookedPeriod) {
            setShowBookPeriodDialogMessage(true)
            return
        }

        const resourceId = resource.id
        const currentResources = localEvent?.resourceIds || []

        const hasOutlookTimeGuard = !!flowConfig?.flow && localEvent && currentResources.length === 0
        if (hasOutlookTimeGuard) {
            const isTimeDifferent = localEvent.start !== startStr || localEvent.end !== endStr

            if (isTimeDifferent) {
                const updatedResourceIds = [resourceId]

                const isFreeAtNewTime = isResourceAvailable(startStr, endStr, updatedResourceIds, localEvent.id)

                if (!isFreeAtNewTime) {
                    enqueueSnackbar({
                        variant: "warning",
                        message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                    })
                    return
                }

                setTimeChangeDialogState({
                    resourceId,
                    newStart: startStr,
                    newEnd: endStr,
                })
                return
            }
        }

        if (localEvent) {
            const updatedResourceIds = currentResources.includes(resourceId)
                ? currentResources
                : [...currentResources, resourceId]

            if (!isResourceAvailable(startStr, endStr, updatedResourceIds, localEvent.id)) {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-error-select"),
                })
                return
            }

            if (currentResources.includes(resourceId)) {
                handleChangeLocalEvent({
                    ...localEvent,
                    start: startStr,
                    end: endStr,
                })

                return
            } else {
                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: [...currentResources, resourceId],
                    start: startStr,
                    end: endStr,
                })

                return
            }
        }

        if (!isResourceAvailable(startStr, endStr, [resourceId])) {
            return
        }

        handleChangeLocalEvent(createNewEvent(resourceId, startStr, endStr))
    }

    const handleEventDrop = (info: EventDropArg) => {
        if (isLoadingEvents) {
            forceFullRevert(info)
            return
        }
        const { event, oldResource } = info
        const { id, extendedProps, start, end } = event

        if (isBookedPeriod && isLocalEvent(id)) {
            setShowBookPeriodDialogMessage(true)
            forceFullRevert(info)
            return
        }

        if (!isLocalEvent(id)) {
            if (isCanEditExistOrder(info)) {
                if (localEvent) {
                    setConfirmAbandonDialogState({ id, title: extendedProps["subject"] })
                } else handleChangeOrderContext(id)
            } else {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-permission-error"),
                })
            }
            forceFullRevert(info)
            return
        }

        const newResourceIds = event.getResources().map((res) => res.id)
        const newStart = DateTime.fromJSDate(start!).toISO() || ""
        const newEnd = DateTime.fromJSDate(end!).toISO() || ""

        if (!isResourceAvailable(newStart, newEnd, newResourceIds, id)) {
            forceFullRevert(info)
            return
        }

        if (localEvent) {
            const currentResourceIds = localEvent.resourceIds
            const draggedResourceId = oldResource?.id

            const updatedResourceIds = draggedResourceId
                ? currentResourceIds
                      .filter((id) => id !== draggedResourceId)
                      .concat(newResourceIds.filter((id) => !currentResourceIds.includes(id)))
                : [...new Set([...currentResourceIds, ...newResourceIds])]

            const updatedEvent = {
                ...localEvent,
                resourceIds: updatedResourceIds,
                start: newStart,
                end: newEnd,
            }

            handleChangeLocalEvent(updatedEvent)
        }
    }

    const toggleResourceSelection = async (resourceId: string) => {
        if (isLoadingEvents) return

        if (isBookedPeriod && localEvent) {
            if (!localEvent.resourceIds.includes(resourceId)) {
                try {
                    const result = callFreeBusyScheduleForResources({
                        userId,
                        availabilityViewInterval: 5,
                        startTime: localEvent.start,
                        endTime: localEvent.end,
                        resourceIds: resourceId,
                    })

                    const isResourceFree = transformFreeBusyScheduleForResources(await result, resourceId)

                    if (isResourceFree) {
                        handleChangeLocalEvent({
                            ...localEvent,
                            resourceIds: [...localEvent.resourceIds, resourceId],
                        })
                    } else {
                        enqueueSnackbar({
                            variant: "warning",
                            message: l10n.getString("create-order-book-period-busy-message"),
                            autoHideDuration: 1500,
                        })
                    }
                    return
                } catch (error) {
                    enqueueSnackbar({
                        variant: "error",
                        message: l10n.getString("create-order-book-period-busy-error"),
                    })
                    return
                }
            } else {
                const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== resourceId)

                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: updatedResourceIds,
                })

                return
            }
        } else {
            if (localEvent) {
                const hasOutlookTimeGuard = !!flowConfig?.flow && localEvent && localEvent?.resourceIds.length === 0
                if (hasOutlookTimeGuard) {
                    if (isInPast(localEvent.start) || isInPast(localEvent.end)) {
                        enqueueSnackbar({
                            variant: "warning",
                            message: l10n.getString("create-order-past-booking-attempt-message"),
                        })
                        return
                    }
                }

                if (localEvent.resourceIds.includes(resourceId)) {
                    const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== resourceId)

                    if (updatedResourceIds.length === 0) {
                        handleChangeLocalEvent(null)
                    } else {
                        handleChangeLocalEvent({
                            ...localEvent,
                            resourceIds: updatedResourceIds,
                        })
                    }
                    return
                }

                if (!isResourceAvailable(localEvent.start, localEvent.end, [resourceId], localEvent.id)) {
                    enqueueSnackbar({
                        variant: "warning",
                        message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                    })
                    return
                }

                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: [...localEvent.resourceIds, resourceId],
                })
            } else {
                let startDateTime: DateTime

                if (
                    selectedDeliveries &&
                    selectedDeliveries.deliveryDate &&
                    selectedDeliveries.deliveryTimes?.length > 0
                ) {
                    const deliveryDate = selectedDeliveries.deliveryDate
                    const time = earliestTime(selectedDeliveries.deliveryTimes) // "HH:mm"

                    startDateTime = dateTimeFactory.fromISO(`${deliveryDate}T${time}`)
                } else {
                    startDateTime = roundDateTimeToNearestMinutes(dateTimeFactory.now(), 5)
                }

                const start = startDateTime.toISO()
                const end = startDateTime.plus({ minutes: DEFAULT_DURATION_MINUTES }).toISO()

                if (!isResourceAvailable(start, end, [resourceId])) {
                    enqueueSnackbar({
                        variant: "warning",
                        message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                    })
                    return
                }

                handleChangeLocalEvent(createNewEvent(resourceId, start, end))
            }
        }
    }

    const handleEventClick = (info: EventClickArg) => {
        const eventId = info.event.id
        const clickedElement = info.jsEvent.target as HTMLElement
        const clickedResourceId = clickedElement?.closest("[data-resource-id]")?.getAttribute("data-resource-id")

        if (!clickedResourceId) {
            return
        }

        if (localEvent && localEvent.id === eventId) {
            const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== clickedResourceId)

            if (updatedResourceIds.length === 0) {
                handleChangeLocalEvent(null)
            } else {
                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: updatedResourceIds,
                })
            }
        }
    }

    const handleEventResize = (info: EventResizeDoneArg) => {
        const { id, start, end, extendedProps } = info.event

        if (isBookedPeriod && isLocalEvent(id)) {
            setShowBookPeriodDialogMessage(true)
            forceFullRevert(info)
            return
        }
        if (!isLocalEvent(id)) {
            if (isCanEditExistOrder(info)) {
                if (localEvent) {
                    setConfirmAbandonDialogState({ id, title: extendedProps["subject"] })
                } else handleChangeOrderContext(id)
            } else {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-permission-error"),
                })
            }
            forceFullRevert(info)
            return
        }

        const newStart = DateTime.fromJSDate(start!).toISO() || ""
        const newEnd = DateTime.fromJSDate(end!).toISO() || ""

        const resourceIds = info.event.getResources().map((res) => res.id)

        if (!isResourceAvailable(newStart, newEnd, resourceIds, id)) {
            forceFullRevert(info)
            return
        }

        handleChangeLocalEvent({
            ...localEvent!,
            start: newStart,
            end: newEnd,
        })
    }

    const resourceLabelContent = (resourceContent: TimelineResourceLabelContent) => (
        <ResourceCalendarLabelContent
            localEvent={localEvent}
            resourceContent={resourceContent}
            searchRegex={searchRegex}
            toggleResourceSelection={toggleResourceSelection}
            handleOpenDetails={openResourceDetailsDialog}
            isLocked={!!(localEventUpdateId && localEvent && isOngoing(localEvent, dateTimeFactory))}
        />
    )

    const resourceGroupLabelContent = (resourceContent: TimelineResourceGroupLabelContent) => (
        <ResourceCalendarGroupContent
            resourceContent={resourceContent}
            groupImagesMap={groupImagesMap}
            onImageClick={openImageModal}
        />
    )

    const handleEventHover = (info: EventHoveringArg | null) => {
        if (isDraggingRef.current) return
        if (!info || !info.el) {
            setHoveredEvent(null)
            return
        }

        const { el } = info
        const { id, start, end, extendedProps } = info.event

        const rect = el.getBoundingClientRect()

        const top = rect.top + window.scrollY - 8
        const left = rect.left + window.scrollX + rect.width / 2

        const checkEvent = isLocalEvent(id)
        setHoveredEvent({
            id,
            top,
            left,
            local: checkEvent,
            start: dateTimeFactory.formatDateTime(DateTime.fromJSDate(start!)),
            end: dateTimeFactory.formatDateTime(DateTime.fromJSDate(end!)),
            host: checkEvent ? undefined : extendedProps["host"],
            subject: checkEvent ? undefined : extendedProps["subject"],
        })
    }

    const handleCloseConfirmAbandonDialog = () => setConfirmAbandonDialogState(null)
    const handleConfirmAbandonDialogClick = () => {
        confirmAbandonDialogState && handleChangeOrderContext(confirmAbandonDialogState.id)
        handleCloseConfirmAbandonDialog()
    }

    const handleKeepOriginalTime = () => {
        if (!timeChangeDialogState || !localEvent) {
            setTimeChangeDialogState(null)
            return
        }

        if (isInPast(localEvent.start) || isInPast(localEvent.end)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-past-booking-attempt-message"),
            })
            return
        }

        const { resourceId } = timeChangeDialogState

        const originalStart = localEvent.start
        const originalEnd = localEvent.end

        const updatedResourceIds = localEvent.resourceIds.includes(resourceId)
            ? localEvent.resourceIds
            : [...localEvent.resourceIds, resourceId]

        if (!isResourceAvailable(originalStart, originalEnd, updatedResourceIds, localEvent.id)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
            })
            setTimeChangeDialogState(null)
            return
        }

        handleChangeLocalEvent({
            ...localEvent,
            start: originalStart,
            end: originalEnd,
            resourceIds: updatedResourceIds,
        })

        setTimeChangeDialogState(null)
    }

    const handleConfirmTimeChange = () => {
        if (!timeChangeDialogState || !localEvent) {
            setTimeChangeDialogState(null)
            return
        }

        const { resourceId, newStart, newEnd } = timeChangeDialogState

        const updatedResourceIds = localEvent.resourceIds.includes(resourceId)
            ? localEvent.resourceIds
            : [...localEvent.resourceIds, resourceId]

        if (!isResourceAvailable(newStart, newEnd, updatedResourceIds, localEvent.id)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
            })
            setTimeChangeDialogState(null)
            return
        }

        handleChangeLocalEvent({
            ...localEvent,
            start: newStart,
            end: newEnd,
            resourceIds: updatedResourceIds,
        })

        setTimeChangeDialogState(null)
    }

    const tooltipContent = useMemo(() => {
        if (!hoveredEvent) return null
        return hoveredEvent.local ? (
            <CalendarLocalEventTooltip start={hoveredEvent.start} end={hoveredEvent.end} />
        ) : (
            <CalendarOrderEventTooltip hoveredEvent={hoveredEvent} />
        )
        // return hoveredEvent.local ? (
        //     <CalendarLocalEventTooltip start={hoveredEvent.start} end={hoveredEvent.end} />
        // ) : isLoadingEventDetails ? (
        //     <CalendarTooltipSkeleton />
        // ) : bookingOrderDetails ? (
        //     <CalendarTooltip details={bookingOrderDetails} />
        // ) : null
        // }, [hoveredEvent, isLoadingEventDetails, bookingOrderDetails])
    }, [hoveredEvent])

    return (
        <>
            <StandardTimelineCalendar
                viewMode="daysGrid"
                localEvents={localEventsSource?.events}
                eventSources={eventSources}
                isLoadingEvents={isLoadingEvents}
                resources={timelineResources}
                handleSelect={handleSelect}
                handleEventDrop={handleEventDrop}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                handleEventClick={handleEventClick}
                handleEventResize={handleEventResize}
                handleEventHover={handleEventHover}
                isBookedPeriod={isBookedPeriod}
                isResourceAvailable={isResourceAvailable}
                selectedDeliveries={selectedDeliveries}
                isDraggingRef={isDraggingRef}
                isUpdateMode={!!localEventUpdateId}
            />

            {!isDraggingRef?.current &&
                hoveredEvent &&
                createPortal(
                    <Box
                        sx={{
                            position: "absolute",
                            top: hoveredEvent.top,
                            left: hoveredEvent.left,
                            transform: "translate(-50%, -100%)",
                            zIndex: 1300,
                            pointerEvents: "none",
                            background: "transparent",
                        }}
                    >
                        {tooltipContent}
                    </Box>,
                    document.body,
                )}

            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
            {resourceDetails && (
                <ResourceDetailsDialog resourceDetails={resourceDetails} handleClose={closeResourceDetailsDialog} />
            )}
            {showBookPeriodDialogMessage && (
                <BookPeriodMessageDialog onClose={() => setShowBookPeriodDialogMessage(false)} />
            )}
            {confirmAbandonDialogState && (
                <LocalizedStrict
                    id={
                        localEventUpdateId
                            ? "create-order-change-current-event-dialog"
                            : "create-order-change-current-event-create-dialog"
                    }
                    attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                    vars={{ orderDetails: `#${confirmAbandonDialogState.id}, ${confirmAbandonDialogState.title}` }}
                >
                    <AbandonBookingDialog
                        title="Confirm booking"
                        message="Are you sure you want to reset changes and update another booking?"
                        handleSubmitClick={handleConfirmAbandonDialogClick}
                        handleCancelClick={handleCloseConfirmAbandonDialog}
                        buttonLabelSubmit="Confirm"
                        buttonLabelCancel="Cancel"
                        open={!!confirmAbandonDialogState}
                        onClose={handleCloseConfirmAbandonDialog}
                    />
                </LocalizedStrict>
            )}
            {timeChangeDialogState && (
                <LocalizedStrict
                    id="create-order-change-time-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                >
                    <AbandonBookingDialog
                        title="Change booking time?"
                        message={
                            <Stack gap="1rem">
                                <Typography variant="body1">
                                    <LocalizedStrict id="create-order-change-time-dialog-message-top">
                                        <>Your Outlook meeting request is scheduled for:</>
                                    </LocalizedStrict>
                                    <Typography fontWeight={500}>
                                        {localEvent
                                            ? dateTimeFactory.formatCompactPeriodFromDateTime(
                                                  dateTimeFactory.fromISO(localEvent.start),
                                                  dateTimeFactory.fromISO(localEvent.end),
                                              )
                                            : ""}
                                    </Typography>
                                </Typography>
                                <Typography variant="body1">
                                    <LocalizedStrict id="create-order-change-time-dialog-message-bottom">
                                        <>Do you want to change booking time to:</>
                                    </LocalizedStrict>
                                    <Typography fontWeight={500}>
                                        {timeChangeDialogState
                                            ? dateTimeFactory.formatCompactPeriodFromDateTime(
                                                  dateTimeFactory.fromISO(timeChangeDialogState.newStart),
                                                  dateTimeFactory.fromISO(timeChangeDialogState.newEnd),
                                              )
                                            : ""}
                                    </Typography>
                                </Typography>
                            </Stack>
                        }
                        handleSubmitClick={handleConfirmTimeChange}
                        handleCancelClick={handleKeepOriginalTime}
                        buttonLabelSubmit="Use new time"
                        buttonLabelCancel="Keep original time"
                        open={!!timeChangeDialogState}
                        onClose={handleKeepOriginalTime}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
